// custom hls video player implementation

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Hls from 'hls.js';


const _k1 = 'pmS0CAMG1Ruq49WbMyhE3fh1sOuLYEL9';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slideIn = keyframes`
  from { 
    transform: translateX(100%);
    opacity: 0;
  }
  to { 
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from { 
    transform: translateY(20px);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeOutDown = keyframes`
  from { 
    transform: translateY(0);
    opacity: 1;
  }
  to { 
    transform: translateY(20px);
    opacity: 0;
  }
`;

// Custom Icon component to replace FontAwesomeIcon
const CustomIcon = styled.img`
  width: ${props => props.$size || '28px'};
  height: ${props => props.$size || '28px'};
  filter: ${props => props.$color ? `brightness(0) invert(1)` : 'none'};
  transition: transform 0.2s;
  ${props => props.$spin && css`animation: ${spin} 1s linear infinite;`}
`;

// Icon path mapping - moved to its own constant to use in preloader
const iconPaths = {
  play: '/icons/1.png',
  pause: '/icons/2.png',
  volumeUp: '/icons/10.png',
  volumeMute: '/icons/11.png',
  volumeDown: '/icons/9.png',
  expand: '/icons/13.png',
  compress: '/icons/15.png',
  settings: '/icons/17.png',
  loop: '/icons/22.png',
  stepForward: '/icons/3.png',
  warning: '/icons/12.png',
  backward: '/icons/5.png',
  forward: '/icons/6.png',
  list: '/icons/18.png',
  check: '/icons/24.png',
  arrowLeft: '/icons/4.png',
  speedometer: '/icons/25.png'
};

// Updated IconPreloader component with improved styling
const IconPreloader = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
`;

// Icon component that maps icon names to image files
const Icon = ({ icon, size, color, spin }) => {
  // For mobile devices, automatically reduce icon size by about 10-15%
  const adjustSizeForMobile = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && size) {
      // Extract numeric value and unit
      const match = size.match(/^(\d+(?:\.\d+)?)(.*)$/);
      if (match) {
        const value = parseFloat(match[1]);
        const unit = match[2] || 'px';
        // Reduce by ~15%
        return `${Math.round(value * 0.85)}${unit}`;
      }
    }
    return size;
  };

  return (
    <CustomIcon 
      src={iconPaths[icon] || iconPaths.play} 
      alt={icon} 
      $size={adjustSizeForMobile()} 
      $color={color} 
      $spin={spin}
    />
  );
};

const PlayerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  color: white;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ControlsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 30%, transparent 70%, rgba(0, 0, 0, 0.8) 100%);
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  z-index: 10;
`;

const TopControls = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 15px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%; // Reduced from 80% to ensure more space for controls
  padding-right: 10px; // Add some padding to prevent text from touching controls
  
  @media (max-width: 768px) {
    font-size: 14px;
    max-width: 60%; // Even smaller on mobile to ensure controls are visible
  }
`;

const TopRightControls = styled.div`
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const SettingsButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    width: 32px;
    height: 32px;
  }
`;

const QualityButton = styled(SettingsButton)`
  font-size: 22px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const BottomControls = styled.div`
  padding: 15px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 5px;
  margin-bottom: 15px;
  cursor: pointer;
  
  &:hover {
    height: 8px;
    transition: height 0.2s ease;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
`;

const BufferedProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 2.5px;
  width: ${props => props.$width || '0%'};
`;

const CurrentProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: var(--primary-light, #3b82f6);
  border-radius: 2.5px;
  width: ${props => props.$width || '0%'};
`;

const ProgressHandle = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => props.$left || '0%'};
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background-color: var(--primary-light, #3b82f6);
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s;
  
  ${ProgressBar}:hover & {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  position: relative;
  
  svg {
    width: 1em;
    height: 1em;
    display: inline-block;
    vertical-align: -0.125em;
    overflow: visible;
  }
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
    
    &::after {
      content: attr(data-tooltip);
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;
      margin-bottom: 8px;
      pointer-events: none;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    width: 32px;
    height: 32px;
  }
`;

const NavigationButton = styled(ControlButton)`
  font-size: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const VolumeButton = styled(ControlButton)`
  width: 42px;
  margin-right: 5px;
  
  svg {
    font-size: 20px;
    width: 1em;
    height: 1em;
  }
  
  @media (max-width: 768px) {
    width: 30px;
    
    svg {
      font-size: 14px;
    }
  }
`;

const PlayButton = styled(ControlButton)`
  font-size: 28px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const TimeDisplay = styled.div`
  margin: 0 15px;
  font-size: 14px;
  font-family: monospace;
  
  @media (max-width: 768px) {
    margin: 0 8px;
    font-size: 12px;
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const VolumeSliderContainer = styled.div`
  position: relative;
  width: 80px;
  margin-left: 10px;
  display: flex;
  align-items: center;
  
  &:hover {
    width: 100px;
    transition: width 0.3s ease;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const VolumeSlider = styled.input`
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  outline: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  box-shadow: none;
  
  /* Chrome/Safari/Edge specific styling */
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    background: white;
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.2s ease;
    border: none;
    margin-top: -4px;
    box-shadow: none;
    
    &:hover {
      transform: scale(1.2);
    }
  }
  
  /* Firefox specific styling */
  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: white;
    border-radius: 50%;
    cursor: pointer;
    border: none;
    transition: transform 0.2s ease;
    box-shadow: none;
    
    &:hover {
      transform: scale(1.2);
    }
  }
  
  &::-webkit-slider-runnable-track {
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    border: none;
  }
  
  &::-moz-range-track {
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    border: none;
  }
  
  /* Remove focus outline */
  &:focus {
    outline: none;
  }
  
  /* Remove focus ring in Firefox */
  &::-moz-focus-outer {
    border: 0;
  }
  
  @media (max-width: 768px) {
    height: 3px;
    
    &::-webkit-slider-thumb {
      width: 10px;
      height: 10px;
      margin-top: -3.5px;
    }
    
    &::-moz-range-thumb {
      width: 10px;
      height: 10px;
    }
  }
`;

const SettingsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 19;
  animation: ${fadeIn} 0.2s ease-out;
`;

const SettingsMenu = styled.div`
  position: absolute;
  top: ${props => props.$fromBottom ? 'auto' : '60px'};
  bottom: ${props => props.$fromBottom ? '70px' : 'auto'};
  right: 15px;
  background-color: rgba(28, 28, 28, 0.9);
  border-radius: 8px;
  padding: 10px;
  width: 240px;
  max-width: calc(100% - 30px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 20;
  animation: ${fadeIn} 0.2s ease-out;
  max-height: 250px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    top: ${props => props.$fromBottom ? 'auto' : '50px'};
    bottom: ${props => props.$fromBottom ? '60px' : 'auto'};
    width: 160px;
    padding: 6px;
    right: 10px;
    max-height: 200px;
  }
  
  @media (max-width: 480px) {
    top: ${props => props.$fromBottom ? 'auto' : '45px'};
    bottom: ${props => props.$fromBottom ? '50px' : 'auto'};
    width: 130px;
    padding: 4px;
    right: 8px;
    max-height: 180px;
  }
`;

const QualityMenu = styled(SettingsMenu)``;

const SpeedMenu = styled(SettingsMenu)``;

const SettingsItem = styled.div`
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 4px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 4px 5px;
    font-size: 11px;
    margin-bottom: 2px;
  }
  
  @media (max-width: 480px) {
    padding: 3px 4px;
    font-size: 10px;
    margin-bottom: 1px;
    border-radius: 3px;
  }
`;

const SettingsLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  
  svg {
    color: var(--primary-light, #3b82f6);
    font-size: 16px;
  }
  
  @media (max-width: 768px) {
    gap: 4px;
    font-size: 12px;
    
    svg {
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    gap: 3px;
    font-size: 11px;
    
    svg {
      font-size: 11px;
    }
  }
`;

const SettingsValue = styled.span`
  color: ${props => props.$active ? 'var(--primary-light, #3b82f6)' : 'rgba(255, 255, 255, 0.7)'};
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const QualityItem = styled(SettingsItem)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  
  @media (max-width: 480px) {
    padding: 3px 4px;
  }
`;

const QualityLabel = styled.span`
  color: ${props => props.$active ? 'var(--primary-light, #3b82f6)' : 'white'};
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 85%;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const CheckIcon = styled.span`
  color: var(--primary-light, #3b82f6);
  opacity: ${props => props.$visible ? 1 : 0};
  flex-shrink: 0;
  width: 18px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 14px;
    
    svg {
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    width: 12px;
    
    svg {
      font-size: 11px;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 30;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: white;
  margin-top: 10px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const RunningGif = styled.img`
  width: auto;
  height: 120px;
  
  @media (max-width: 768px) {
    height: 100px;
  }
  
  /* Prevent flickering on image load error */
  &.fallback {
    width: 120px;
    height: 120px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const BufferingIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 15;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const BufferingGif = styled.img`
  width: auto;
  height: 100px;
  
  @media (max-width: 768px) {
    height: 80px;
  }
  
  /* Prevent flickering on image load error */
  &.fallback {
    width: 100px;
    height: 100px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const BufferingText = styled.div`
  font-size: 12px;
  color: white;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
  font-weight: 500;
  margin-top: 10px;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 30;
  padding: 20px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  color: #ff5555;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ErrorMessage = styled.div`
  font-size: 18px;
  color: white;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ErrorButton = styled.button`
  background-color: var(--primary-color, #3b82f6);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: var(--border-radius, 4px);
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-light, #4b92ff);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }
`;

const EndedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const ReplayButton = styled.button`
  background-color: var(--primary-color, #3b82f6);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  font-size: 42px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  
  &:hover {
    background-color: var(--primary-light, #4b92ff);
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }
`;

const NextButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 20px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

const CenteredPlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: transparent;
  border: none;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => props.$visible ? 1 : 0};
  z-index: 20;
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
  }
  
  svg {
    font-size: 60px;
    color: white;
    filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.7));
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    
    svg {
      font-size: 32px;
    }
  }
`;

const PreviewThumbnail = styled.div`
  position: absolute;
  bottom: 100%;
  left: ${props => props.$position}px;
  transform: translateX(-50%);
  width: 160px;
  height: 90px;
  background-color: #000;
  border: 2px solid white;
  border-radius: 4px;
  overflow: hidden;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TimeTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: ${props => props.$position}px;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 8px;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
`;

const SpeedItem = styled(SettingsItem)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  
  @media (max-width: 480px) {
    padding: 3px 4px;
  }
`;

const SpeedLabel = styled.span`
  color: ${props => props.$active ? 'var(--primary-light, #3b82f6)' : 'white'};
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const ContinueButton = styled.button`
  position: absolute;
  bottom: 80px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
  z-index: 30;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.8s ease forwards;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
  }
  
  &.fade-out {
    animation: ${fadeOut} 0.8s ease forwards;
  }
  
  @media (max-width: 768px) {
    bottom: 65px;
    right: 12px;
    font-size: 11px;
    padding: 8px 12px;
    gap: 6px;
    border-radius: 6px;
    
    img {
      width: 14px;
      height: 14px;
    }
  }
`;

// Add decryption utility - deliberately named something generic
const processSecureUrl = async (url) => {
  // Check if the URL needs decryption
  if (!url || !url.startsWith('enc2:')) {
    return url;
  }
  
  try {
    // Get part 2 of the key from a different location in the code
    // This makes it harder for someone to find the complete key
    const _k2 = 'rtFazYYljVI2j4BPSog73hW7A7xMhceHD0iwrPrVVDXLvxyWr';
    const fullKey = _k1 + _k2;
    
    console.log('Processing secure URL with additional encryption');
    
    // Extract the encrypted part
    const encryptedPart = url.substring(5);
    
    // Decode from base64
    const encoded = encryptedPart.replace(/-/g, '+').replace(/_/g, '/');
    let paddedEncoded = encoded;
    while (paddedEncoded.length % 4) {
      paddedEncoded += '=';
    }
    
    const combined = Uint8Array.from(atob(paddedEncoded), (c) => c.charCodeAt(0));
    
    // Extract IV and ciphertext
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    
    // Create a key from the encryption key
    const encoder = new TextEncoder();
    const keyData = await crypto.subtle.digest(
      'SHA-256',
      encoder.encode(fullKey)
    );
    
    // Import the key
    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    
    // Decrypt the data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv
      },
      key,
      ciphertext
    );
    
    // Return the decrypted URL
    const decryptedUrl = new TextDecoder().decode(decrypted);
    console.log(`Decrypted URL: ${decryptedUrl.substring(0, 30)}...`);
    return decryptedUrl;
  } catch (error) {
    console.error('Error processing secure URL:', error);
    return url; // Return the original URL on error
  }
};

// Modify the handlePlaylist function to decrypt URLs
async function processM3u8Content(content, baseUrl) {
  const lines = content.split('\n');
  const newLines = [];
  
  console.log('Processing M3U8 content for additional encryption');
  let encryptedLinesFound = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (!line) {
      newLines.push('');
      continue;
    }
    
    // For non-comment lines that might be URLs, try to decrypt them
    if (!line.startsWith('#')) {
      // Check if this line is an encrypted URL
      if (line.startsWith('enc2:')) {
        encryptedLinesFound = true;
        console.log(`Found encrypted URL: ${line.substring(0, 30)}...`);
        
        // Process the URL to decrypt if needed
        const processedUrl = await processSecureUrl(line);
        newLines.push(processedUrl);
      } else {
        // Not encrypted, pass through
        newLines.push(line);
      }
    } else if (line.startsWith('#EXT-X-KEY') || line.startsWith('#EXT-X-MAP') || line.startsWith('#EXT-X-MEDIA')) {
      // For lines with URI attributes, decrypt the URI if needed
      const uriMatch = line.match(/URI="([^"]+)"/);
      if (uriMatch && uriMatch[1] && uriMatch[1].startsWith('enc2:')) {
        encryptedLinesFound = true;
        console.log(`Found encrypted URI in: ${line}`);
        
        const decryptedUri = await processSecureUrl(uriMatch[1]);
        const newLine = line.replace(/URI="([^"]+)"/, `URI="${decryptedUri}"`);
        newLines.push(newLine);
      } else {
        // Not encrypted, pass through
        newLines.push(line);
      }
    } else {
      // Pass through all other lines unchanged
      newLines.push(line);
    }
  }
  
  console.log(`M3U8 processing complete. Encrypted lines found: ${encryptedLinesFound}`);
  return newLines.join('\n');
}

const Player = forwardRef(({ 
  token,
  autoPlay = true,
  loop = false,
  autoNext = true,
  sources = [],
  title = '', // Show title
  episodeTitle = '', // Episode title
  episodeNumber = null, // Episode number
  seasonNumber = null, // Season number
  onReady,
  onPlayPause,
  onTimeUpdate,
  onEnded,
  onVolumeChange,
  onMuteToggle,
  onFullscreenToggle,
  onQualityChange,
  onLoopChange,
  onAutoNextChange,
  onPlaybackSpeedChange,
  onNext,
  onPrevious
}, ref) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const hlsRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const qualityBtnRef = useRef(null);
  const speedBtnRef = useRef(null);
  
  // Preloaded state tracking
  const [iconsPreloaded, setIconsPreloaded] = useState(false);
  const preloadedIcons = useRef(new Set());
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedTime, setBufferedTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [error, setError] = useState(null);
  const [hasEnded, setHasEnded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Initializing video player...");
  
  // Settings menu state
  const [availableQualities, setAvailableQualities] = useState(['auto']);
  const [currentQuality, setCurrentQuality] = useState('auto');
  const [autoSelectedQuality, setAutoSelectedQuality] = useState('');
  const [isLoopEnabled, setIsLoopEnabled] = useState(loop);
  const [isAutoNextEnabled, setIsAutoNextEnabled] = useState(autoNext);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [availableSpeeds] = useState([0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0]);
  
  const controlsTimerRef = useRef(null);
  const seekingRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const bufferingTimerRef = useRef(null);
  const tapTimeoutRef = useRef(null);
  
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState(0);
  const [previewTime, setPreviewTime] = useState(0);
  const [isHoveringProgress, setIsHoveringProgress] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(0);
  const [tooltipTime, setTooltipTime] = useState(0);
  
  const [loadingImageError, setLoadingImageError] = useState(false);
  const [bufferingImageError, setBufferingImageError] = useState(false);
  const lastBufferingStateRef = useRef(false);
  
  const [savedProgress, setSavedProgress] = useState(null);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const continueButtonRef = useRef(null);
  const hasShownContinueButton = useRef(false);
  
  // Add ref to track the current token for cleanup
  const currentTokenRef = useRef(token);
  const [isRotationSupported, setIsRotationSupported] = useState(false);
  
  // Update current token ref when token changes
  useEffect(() => {
    // If token has changed, clear saved progress for the previous token
    if (currentTokenRef.current && currentTokenRef.current !== token) {
      console.log(`Token changed from ${currentTokenRef.current} to ${token}, clearing previous saved progress`);
      localStorage.removeItem(`video_progress_${currentTokenRef.current}`);
    }
    
    // Update the current token ref
    currentTokenRef.current = token;
    
    // Reset hasShownContinueButton when token changes
    hasShownContinueButton.current = false;
  }, [token]);
  
  // Check if screen rotation is supported
  useEffect(() => {
    // Check if the Screen Orientation API is supported
    // Use a safer approach that doesn't cause navigation issues
    const checkRotationSupport = () => {
      try {
        return typeof window !== 'undefined' && 
          window.screen && 
          (window.screen.orientation || 
          window.screen.mozOrientation || 
          window.screen.msOrientation);
      } catch (err) {
        console.warn('Error checking rotation support:', err);
        return false;
      }
    };
    
    setIsRotationSupported(checkRotationSupport());
  }, []);
  
  // Create a unique progress key that includes episode info to prevent conflicts
  const getProgressKey = () => {
    // Create a unique key using token and episode information
    let key = `video_progress_${token}`;
    
    // Add episode identifiers if available to make each episode's progress unique
    if (episodeNumber) key += `_ep${episodeNumber}`;
    if (seasonNumber) key += `_s${seasonNumber}`;
    
    return key;
  };
  
  // Load saved progress when component mounts
  useEffect(() => {
    if (token) {
      const progressKey = getProgressKey();
      const savedData = localStorage.getItem(progressKey);
      if (savedData) {
        try {
          const { time, duration } = JSON.parse(savedData);
          setSavedProgress({ time, duration });
        } catch (err) {
          console.error("Error parsing saved progress:", err);
          // Remove corrupted data
          localStorage.removeItem(progressKey);
        }
      } else {
        // Reset saved progress if none found for this episode
        setSavedProgress(null);
      }
    }
  }, [token, episodeNumber, seasonNumber]);
  
  // Save progress periodically
  useEffect(() => {
    if (!videoRef.current || !token) return;
    
    const saveProgress = () => {
      if (videoRef.current && videoRef.current.currentTime > 0) {
        const progress = {
          time: videoRef.current.currentTime,
          duration: videoRef.current.duration
        };
        
        const progressKey = getProgressKey();
        localStorage.setItem(progressKey, JSON.stringify(progress));
      }
    };
    
    // Save every 5 seconds
    const interval = setInterval(saveProgress, 5000);
    
    return () => clearInterval(interval);
  }, [token, episodeNumber, seasonNumber]);
  
  // Clear saved progress when video ends
  useEffect(() => {
    if (hasEnded && token) {
      const progressKey = getProgressKey();
      localStorage.removeItem(progressKey);
      setSavedProgress(null);
    }
  }, [hasEnded, token, episodeNumber, seasonNumber]);
  
  // Show continue button after video is ready
  useEffect(() => {
    if (videoReady && savedProgress && !hasShownContinueButton.current) {
      // Small delay before showing the button
      const showTimeout = setTimeout(() => {
        setShowContinueButton(true);
        hasShownContinueButton.current = true;
        
        // Auto-hide continue button after 5 seconds
        const hideTimeout = setTimeout(() => {
          if (continueButtonRef.current) {
            continueButtonRef.current.classList.add('fade-out');
            setTimeout(() => {
              setShowContinueButton(false);
            }, 800); // Match the animation duration
          }
        }, 5000);

        return () => clearTimeout(hideTimeout);
      }, 500); // Reduced delay to 500ms

      return () => clearTimeout(showTimeout);
    }
  }, [videoReady, savedProgress]);
  
  // Handle continue from saved progress
  const handleContinue = (e) => {
    e.stopPropagation();
    if (videoRef.current && savedProgress) {
      videoRef.current.currentTime = savedProgress.time;
      videoRef.current.play().catch(err => {
        console.error('Failed to play:', err);
      });
      setShowContinueButton(false);
    }
  };
  
  // Reset hasShownContinueButton when token changes
  useEffect(() => {
    hasShownContinueButton.current = false;
  }, [token]);
  
  // Format title to include episode info in the format (EpisodeNumber) EpisodeTitle - ShowTitle S(SeasonNumber)
  const formatTitleDisplay = () => {
    if (!episodeNumber && !episodeTitle && !seasonNumber) {
      // Just return the title if we don't have episode information
      return title;
    }
    
    // Build the title pieces
    const episodePrefix = episodeNumber ? `EP${episodeNumber} - ` : '';
    const episodePart = episodeTitle ? `${episodePrefix}${episodeTitle}` : (episodeNumber ? `${episodePrefix}Episode ${episodeNumber}` : '');
    const seasonPart = seasonNumber ? ` - S${seasonNumber}` : '';
    const showPart = title ? ` - ${title}` : '';
    
    // Combine all parts
    const fullTitle = `${episodePart}${seasonPart}${showPart}`;
    
    // Check if the title is too long (more than 50 chars) and truncate if needed
    if (fullTitle.length > 50) {
      return `${fullTitle.substring(0, 47)}...`;
    }
    
    return fullTitle;
  };
  
  // close all modals
  const closeAllModals = () => {
    setShowSettings(false);
    setShowQualityMenu(false);
    setShowSpeedMenu(false);
  };
  
  // MM:SS
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return '00:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // progress percentage
  const getProgressPercentage = () => {
    if (duration === 0) return 0;
    return (currentTime / duration) * 100;
  };
  
  // buffered percentage
  const getBufferedPercentage = () => {
    if (duration === 0) return 0;
    return (bufferedTime / duration) * 100;
  };
  
  // play/pause toggle
  const togglePlayPause = (e) => {
    if (e) e.stopPropagation();
    
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        const playPromise = videoRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.error("Play error:", err);
            setError('Failed to play video. Please try again.');
          });
        }
      }
    }
  };
  
  //seeking in progress bar
  const handleProgressBarClick = (e) => {
    if (!progressBarRef.current || !videoRef.current || duration === 0) return;
    
    e.stopPropagation();
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const newTime = clickPosition * duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    
    // restart playback when ended
    if (hasEnded) {
      setHasEnded(false);
      videoRef.current.play().catch(err => {
        setError('Failed to play video. Please try again.');
      });
    }
  };
  
  // volume change
  const handleVolumeChange = (e) => {
    e.stopPropagation();
    
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      
      // If volume is set to 0 mute the video
      if (newVolume === 0) {
        setIsMuted(true);
        videoRef.current.muted = true;
      } else if (isMuted) {
        // volume increased from 0 unmute the video
        setIsMuted(false);
        videoRef.current.muted = false;
      }
    }
    
    if (onVolumeChange) {
      onVolumeChange(newVolume);
    }
  };
  
  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    
    if (videoRef.current) {
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      videoRef.current.muted = newMutedState;
      
      if (onMuteToggle) {
        onMuteToggle(newMutedState);
      }
    }
  };
  
  // fullscreen toggle
  const toggleFullscreen = (e) => {
    if (e) e.stopPropagation();
    
    if (!containerRef.current) return;
    
    // Check if we're already in fullscreen
    const isFullscreenActive = !!document.fullscreenElement ||
                              !!document.webkitFullscreenElement ||
                              !!document.mozFullScreenElement ||
                              !!document.msFullscreenElement;
    
    // On mobile, try to rotate screen to landscape when entering fullscreen
    const tryRotateScreen = (toFullscreen) => {
      // Skip if rotation is not supported or not on mobile
      if (!isRotationSupported || window.innerWidth > 1024) return;
      
      try {
        const orientation = window.screen.orientation || 
                          window.screen.mozOrientation || 
                          window.screen.msOrientation;
        
        if (orientation && orientation.lock && toFullscreen) {
          // Only attempt to lock orientation, don't wait for promise
          orientation.lock('landscape').catch(err => {
            console.warn('Screen rotation not allowed:', err);
          });
        } else if (orientation && orientation.unlock && !toFullscreen) {
          try {
            orientation.unlock();
          } catch (err) {
            console.warn('Error unlocking orientation:', err);
          }
        }
      } catch (err) {
        console.warn('Error manipulating screen orientation:', err);
      }
    };
    
    // Enter fullscreen with safer implementation
    if (!isFullscreenActive) {
      try {
        // Try the standard method first
        if (containerRef.current.requestFullscreen) {
          containerRef.current.requestFullscreen()
            .then(() => {
              setIsFullscreen(true);
              if (onFullscreenToggle) onFullscreenToggle(true);
              
              // Try to rotate screen only after fullscreen is successful
              setTimeout(() => tryRotateScreen(true), 300);
            })
            .catch(err => {
              console.error("Fullscreen error:", err);
            });
        } 
        // Fallbacks for various browsers
        else if (containerRef.current.webkitRequestFullscreen) {
          containerRef.current.webkitRequestFullscreen();
          setIsFullscreen(true);
          if (onFullscreenToggle) onFullscreenToggle(true);
          setTimeout(() => tryRotateScreen(true), 300);
        } 
        else if (containerRef.current.mozRequestFullScreen) {
          containerRef.current.mozRequestFullScreen();
          setIsFullscreen(true);
          if (onFullscreenToggle) onFullscreenToggle(true);
          setTimeout(() => tryRotateScreen(true), 300);
        } 
        else if (containerRef.current.msRequestFullscreen) {
          containerRef.current.msRequestFullscreen();
          setIsFullscreen(true);
          if (onFullscreenToggle) onFullscreenToggle(true);
          setTimeout(() => tryRotateScreen(true), 300);
        }
      } catch (err) {
        console.error("Error entering fullscreen:", err);
      }
    } 
    // Exit fullscreen with safer implementation
    else {
      try {
        // Unlock orientation first
        tryRotateScreen(false);
        
        // Then exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen()
            .then(() => {
              setIsFullscreen(false);
              if (onFullscreenToggle) onFullscreenToggle(false);
            })
            .catch(err => {
              console.error("Exit fullscreen error:", err);
              setIsFullscreen(false);
              if (onFullscreenToggle) onFullscreenToggle(false);
            });
        } 
        else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
          setIsFullscreen(false);
          if (onFullscreenToggle) onFullscreenToggle(false);
        } 
        else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
          setIsFullscreen(false);
          if (onFullscreenToggle) onFullscreenToggle(false);
        } 
        else if (document.msExitFullscreen) {
          document.msExitFullscreen();
          setIsFullscreen(false);
          if (onFullscreenToggle) onFullscreenToggle(false);
        }
      } catch (err) {
        console.error("Error exiting fullscreen:", err);
      }
    }
  };
  
  // controls timer
  const resetControlsTimer = () => {
    lastActivityRef.current = Date.now();
    
    if (controlsTimerRef.current) {
      clearTimeout(controlsTimerRef.current);
    }
    
    setShowControls(true);
    
    controlsTimerRef.current = setTimeout(() => {
      // hide controls if playing and not showing settings
      if (isPlaying && !showSettings && !showQualityMenu && !showSpeedMenu && !seekingRef.current) {
        setShowControls(false);
      }
    }, 3000);
  };
  
  const handleMouseMove = () => {
    resetControlsTimer();
  };
  
  const toggleSettings = (e) => {
    if (e) e.stopPropagation();
    setShowSettings(!showSettings);
    setShowQualityMenu(false);
    setShowSpeedMenu(false);
    resetControlsTimer();
  };

  // toggle quality menu
  const toggleQualityMenu = (e) => {
    if (e) e.stopPropagation();
    setShowQualityMenu(!showQualityMenu);
    setShowSettings(false);
    setShowSpeedMenu(false);
    resetControlsTimer();
  };
  
  // loop toggle
  const toggleLoop = (e) => {
    if (e) e.stopPropagation();
    
    const newLoopState = !isLoopEnabled;
    setIsLoopEnabled(newLoopState);
    
    if (videoRef.current) {
      videoRef.current.loop = newLoopState;
    }
    
    if (onLoopChange) {
      onLoopChange(newLoopState);
    }
  };
  
  // auto next toggle
  const toggleAutoNext = (e) => {
    if (e) e.stopPropagation();
    
    const newAutoNextState = !isAutoNextEnabled;
    setIsAutoNextEnabled(newAutoNextState);
    
    if (onAutoNextChange) {
      onAutoNextChange(newAutoNextState);
    }
  };
  
  // quality change
  const changeQuality = (quality) => {
    console.log('Changing quality to:', quality);
    setCurrentQuality(quality);
    
    if (hlsRef.current) {
      if (quality === 'auto') {
        console.log('Setting HLS to auto level');
        if (hlsRef.current && hlsRef.current.levels) {
          hlsRef.current.currentLevel = -1; // auto
          
          // When switching to auto, capture the current auto-selected quality if available
          if (hlsRef.current.currentLevel !== -1 && hlsRef.current.levels[hlsRef.current.currentLevel]) {
            setAutoSelectedQuality(`${hlsRef.current.levels[hlsRef.current.currentLevel].height}p`);
          } else {
            setAutoSelectedQuality(''); // Reset if no current level info available
          }
        }
      } else {
        // level index that matches the selected quality
        const height = parseInt(quality.replace('p', ''));
        console.log('Looking for quality with height:', height);
        
        if (hlsRef.current && hlsRef.current.levels) {
          const levelIndex = hlsRef.current.levels.findIndex(level => 
            level.height === height
          );
          
          console.log('Found level index:', levelIndex);
          
          if (levelIndex !== -1) {
            hlsRef.current.currentLevel = levelIndex;
            console.log('Set HLS level to:', levelIndex);
            setAutoSelectedQuality(''); // Clear auto-selected quality when manually setting
          } else {
            console.warn('Could not find matching quality level');
          }
        }
      }
    }
    
    setShowQualityMenu(false);
    
    // Notify main component
    if (onQualityChange) {
      onQualityChange(quality);
    }
  };
  
  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    
    // Reload
    if (videoRef.current) {
      videoRef.current.load();
    }
  };
  
  // Handle replay on ended
  const handleReplay = () => {
    if (videoRef.current) {
      setHasEnded(false);
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => {
        setError('Failed to play video. Please try again.');
      });
    }
  };
  
  const handleDoubleTap = (direction) => {
    if (!videoRef.current) return;
    
    if (direction === 'forward') {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration,
        videoRef.current.currentTime + 10
      );
    } else if (direction === 'backward') {
      videoRef.current.currentTime = Math.max(
        0,
        videoRef.current.currentTime - 10
      );
    }
  };
  
  // toggle speed menu
  const toggleSpeedMenu = (e) => {
    if (e) e.stopPropagation();
    setShowSpeedMenu(!showSpeedMenu);
    setShowSettings(false);
    setShowQualityMenu(false);
    resetControlsTimer();
  };
  
  // change playback speed
  const changePlaybackSpeed = (speed) => {
    console.log('Changing playback speed to:', speed);
    setPlaybackSpeed(speed);
    
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
    
    setShowSpeedMenu(false);
    
    if (onPlaybackSpeedChange) {
      onPlaybackSpeedChange(speed);
    }
  };
  
  // methods to parent via ref [exposed to main]
  useImperativeHandle(ref, () => ({
    play: () => {
      if (videoRef.current) {
        videoRef.current.play().catch(err => {
          console.error('Failed to play:', err);
        });
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    seekTo: (time) => {
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
    },
    getDuration: () => duration,
    getCurrentTime: () => currentTime,
    getIsPlaying: () => isPlaying,
    setVolume: (vol) => {
      if (videoRef.current) {
        const newVol = Math.max(0, Math.min(1, vol));
        videoRef.current.volume = newVol;
        setVolume(newVol);
      }
    },
    mute: () => {
      if (videoRef.current) {
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    },
    unmute: () => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        setIsMuted(false);
      }
    },
    requestFullscreen: () => {
      if (containerRef.current) {
        containerRef.current.requestFullscreen().catch(err => {
          console.error('Failed to enter fullscreen:', err);
        });
      }
    },
    exitFullscreen: () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(err => {
          console.error('Failed to exit fullscreen:', err);
        });
      }
    },
    setLoop: (loopState) => {
      if (videoRef.current) {
        videoRef.current.loop = loopState;
        setIsLoopEnabled(loopState);
      }
    },
    setAutoNext: (autoNextState) => {
      setIsAutoNextEnabled(autoNextState);
    },
    getAvailableQualities: () => availableQualities,
    setQuality: (quality) => {
      if (hlsRef.current) {
        changeQuality(quality);
      }
    },
    setPlaybackSpeed: (speed) => {
      if (videoRef.current) {
        changePlaybackSpeed(speed);
      }
    },
    getPlaybackSpeed: () => playbackSpeed,
    closeAllModals: closeAllModals
  }));
  
  // Initialize HLS.js [if needed]
  useEffect(() => {
    if (!token) {
      console.warn("No token provided to Player component");
      setError("No video source provided");
      setIsLoading(false);
      return;
    }
    
    const videoElement = videoRef.current;
    if (!videoElement) return;
    
    setIsLoading(true);
    setVideoReady(false);
    setLoadingMessage("Initializing video stream...");
    setError(null);
    setHasEnded(false);
    setAvailableQualities([]);
    setCurrentQuality('auto');
    
    // Create a unique identifier for this token to prevent duplicate initializations
    const tokenId = token.substring(0, 20); // Use part of the token as an ID
    
    // v1.m3u8lock.workers.dev
    const url = `https://v1.m3u8lock.workers.dev/playlist/${token}`;
    console.log("Loading HLS stream from:", url);
    
    // Clean up any HLS instance
    if (hlsRef.current) {
      console.log("Destroying previous HLS instance");
      try {
        hlsRef.current.destroy();
      } catch (err) {
        console.error("Error destroying previous HLS instance:", err);
      }
      hlsRef.current = null;
    }
    
    let isMounted = true;
    let hls = null;
    
    // Improved browser compatibility check
    const initializeNativeHLS = () => {
      // Native HLS support (Safari, iOS, etc.)
      console.log("Using native HLS support");
      
      // For native HLS support, we need to intercept the playlist request
      // to handle additional encryption
      const fetchAndProcessPlaylist = async () => {
        try {
          setLoadingMessage("Fetching playlist...");
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch playlist: ${response.status}`);
          }
          
          const content = await response.text();
          setLoadingMessage("Processing playlist...");
          
          // Process the playlist to handle additional encryption
          const processedContent = await processM3u8Content(content, url);
          
          // Create a blob URL from the processed content
          const blob = new Blob([processedContent], { type: 'application/vnd.apple.mpegurl' });
          const blobUrl = URL.createObjectURL(blob);
          
          // Set the video source to the blob URL
          videoElement.src = blobUrl;
          
          // Clean up the blob URL when the component unmounts
          return () => {
            URL.revokeObjectURL(blobUrl);
          };
        } catch (error) {
          console.error("Error fetching and processing playlist:", error);
          setError(`Failed to load video: ${error.message}`);
          setIsLoading(false);
          return () => {};
        }
      };
      
      // Start fetching and processing
      const cleanup = fetchAndProcessPlaylist();
      
      videoElement.addEventListener('loadedmetadata', () => {
        if (!isMounted) return;
        setLoadingMessage("Video metadata loaded, preparing playback...");
        if (autoPlay) {
          videoElement.play().catch(err => {
            console.error('Failed to autoplay:', err);
            // Try playing muted if autoplay failed
            if (isMounted) {
              videoElement.muted = true;
              setIsMuted(true);
              videoElement.play().catch(mutedErr => {
                console.error('Failed to autoplay even when muted:', mutedErr);
              });
            }
          });
        }
        
        // Set video as ready
        setIsLoading(false);
        setVideoReady(true);
        if (onReady) onReady();
        
        // Native HLS doesn't support quality selection
        setAvailableQualities(['auto']);
      });
      
      videoElement.addEventListener('error', (e) => {
        if (!isMounted) return;
        console.error('Native HLS error:', videoElement.error);
        setError(`Failed to load video: ${videoElement.error?.message || 'Unknown error'}`);
        setIsLoading(false);
      });
      
      return () => {
        cleanup();
      };
    };
    
    // First check if the browser has MediaSource Extensions support
    const hasMSE = 'MediaSource' in window || 'WebKitMediaSource' in window;
    
    // Check for HLS.js support with more reliable conditions
    if (hasMSE && typeof Hls !== 'undefined' && Hls.isSupported()) {
      try {
        // Configure HLS.js with more compatible settings
        hls = new Hls({
          // Default settings that work well across browsers
          maxBufferLength: 30,        
          maxMaxBufferLength: 60,
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 60,
          startLevel: -1,            // auto quality [default]
          debug: false,
          xhrSetup: function(xhr) {
            // Add CORS headers for some environments
            xhr.withCredentials = false;
          },
          // Add a custom loader to handle additional encryption
          loader: class CustomLoader extends Hls.DefaultConfig.loader {
            constructor(config) {
              super(config);
              const load = this.load.bind(this);
              
              this.load = function(context, config, callbacks) {
                const onSuccess = callbacks.onSuccess;
                
                callbacks.onSuccess = async function(response, stats, context, networkDetails) {
                  // Only process m3u8 files
                  if (context.type === 'manifest' || context.type === 'level' || context.responseType === 'text') {
                    const contentType = networkDetails?.headers?.['content-type'];
                    
                    if (response.data && (
                      (contentType && contentType.includes('mpegurl')) || 
                      context.url.includes('.m3u8') || 
                      (typeof response.data === 'string' && response.data.includes('#EXTM3U'))
                    )) {
                      try {
                        // Process the m3u8 content to decrypt any encrypted URLs
                        const originalContent = typeof response.data === 'string' 
                          ? response.data 
                          : new TextDecoder().decode(response.data);
                        
                        console.log(`Processing HLS content from ${context.url}`);
                        const processedContent = await processM3u8Content(originalContent, context.url);
                        
                        // Replace the response data with the processed content
                        if (typeof response.data === 'string') {
                          response.data = processedContent;
                        } else {
                          response.data = new TextEncoder().encode(processedContent);
                        }
                      } catch (error) {
                        console.error('Error processing m3u8 content:', error);
                        // Continue with original content if processing fails
                      }
                    }
                  }
                  
                  // Call the original onSuccess callback
                  onSuccess(response, stats, context, networkDetails);
                };
                
                // Call the original load function
                load(context, config, callbacks);
              };
            }
          },
          // More conservative error recovery
          fragLoadingMaxRetry: 8,
          fragLoadingRetryDelay: 1000, 
          fragLoadingMaxRetryTimeout: 10000,
          manifestLoadingMaxRetry: 8,
          manifestLoadingRetryDelay: 1000,
          manifestLoadingMaxRetryTimeout: 10000,
          levelLoadingMaxRetry: 8,
          levelLoadingRetryDelay: 1000,
          // More compatible buffer control parameters
          abrEwmaDefaultEstimate: 500000, // Start with lower bandwidth estimate
          abrBandWidthFactor: 0.9,
          abrBandWidthUpFactor: 0.7,
          maxStarvationDelay: 4,
          maxLoadingDelay: 4,
        });
        
        hlsRef.current = hls;
        
        let manifestLoaded = false;
        let recoveryAttempts = 0;
        const maxRecoveryAttempts = 6;
        
        hls.loadSource(url);
        hls.attachMedia(videoElement);
        
        hls.on(Hls.Events.MANIFEST_LOADING, () => {
          if (!isMounted) return;
          setLoadingMessage("Loading video manifest...");
        });
        
        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          if (!isMounted) return;
          console.log('HLS Manifest Parsed, levels:', hls.levels);
          manifestLoaded = true;
          recoveryAttempts = 0; // Reset recovery attempts on successful manifest load
          setLoadingMessage("Preparing video playback...");
          
          // Set initial quality to auto for better compatibility
          if (hls && hls.levels && hls.levels.length > 0) {
            try {
              hls.currentLevel = -1; // Auto level
            } catch (err) {
              console.error("Error setting initial quality level:", err);
            }
          }
          
          if (autoPlay && isMounted) {
            videoElement.play().catch(err => {
              console.error('Failed to autoplay:', err);
              // Try playing muted if autoplay failed
              if (isMounted) {
                videoElement.muted = true;
                setIsMuted(true);
                videoElement.play().catch(mutedErr => {
                  console.error('Failed to autoplay even when muted:', mutedErr);
                });
              }
            });
          }
          
          // available qualities
          if (hls && hls.levels && hls.levels.length > 0 && isMounted) {
            const qualities = hls.levels.map(level => `${level.height}p`);
            console.log('Available qualities:', qualities);
            setAvailableQualities(['auto', ...qualities]);
          }
          
          // Don't hide loading here - wait for canplay event
        });
        
        // Add a timeout to handle cases where manifest doesn't load
        const manifestTimeout = setTimeout(() => {
          if (!manifestLoaded && isMounted) {
            console.warn("HLS manifest load timed out, retrying...");
            setLoadingMessage("Connection slow, retrying...");
            // Try to recover
            if (hls) {
              try {
                hls.stopLoad();
                hls.loadSource(url);
                hls.startLoad();
              } catch (err) {
                console.error("Error during manifest timeout recovery:", err);
                // If HLS.js fails, fall back to native implementation if possible
                if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                  try {
                    hls.destroy();
                    hlsRef.current = null;
                    initializeNativeHLS();
                  } catch (fallbackErr) {
                    console.error("Error falling back to native HLS:", fallbackErr);
                    setError('Failed to load video. Please try refreshing the page.');
                    setIsLoading(false);
                  }
                } else {
                  setError('Failed to load video. Please try refreshing the page.');
                  setIsLoading(false);
                }
              }
            }
          }
        }, 8000);
        
        hls.on(Hls.Events.LEVEL_LOADING, (event, data) => {
          if (!isMounted) return;
          setLoadingMessage("Loading video quality level...");
        });
        
        hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
          if (!isMounted) return;
          console.log('HLS Level loaded:', data.level);
          
          if (hls && data.level === -1) {
            setCurrentQuality('auto');
          } else if (hls && hls.levels && hls.levels[data.level]) {
            const currentHeight = `${hls.levels[data.level].height}p`;
            
            // Check if in auto level mode (currentLevel === -1)
            if (hls.autoLevelEnabled || hls.currentLevel === -1) {
              // If in auto mode, update auto-selected quality but maintain auto as the user selection
              setAutoSelectedQuality(currentHeight);
            } else {
              // Manual quality selection
              setCurrentQuality(currentHeight);
            }
          }
        });
        
        hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
          if (!isMounted) return;
          console.log('HLS Level switched to:', data.level);
          
          // Check if current mode is auto (currentLevel === -1)
          const isAutoMode = hls.autoLevelEnabled || hls.currentLevel === -1;
          
          if (isAutoMode) {
            // We're in auto mode, keep auto as the selection but track the auto-selected quality
            setCurrentQuality('auto');
            
            if (hls.levels && hls.levels[data.level]) {
              const currentHeight = `${hls.levels[data.level].height}p`;
              setAutoSelectedQuality(currentHeight);
            }
          } else if (hls && hls.levels && hls.levels[data.level]) {
            // Manual mode - update the current quality to match the selected level
            const currentHeight = `${hls.levels[data.level].height}p`;
            setCurrentQuality(currentHeight);
          }
        });
        
        // Improved buffer monitoring
        hls.on(Hls.Events.BUFFER_APPENDED, () => {
          if (!isMounted) return;
          // When buffer is appended, maybe we can clear buffering state
          if (videoElement.readyState >= 3 && isBuffering) {
            setIsBuffering(false);
          }
        });
        
        // Improved error handling
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (!isMounted) return;
          console.error('HLS Error:', data);
          
          // Handle fragment loading errors specifically
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (data.details === Hls.ErrorDetails.FRAG_LOAD_ERROR || 
                data.details === Hls.ErrorDetails.FRAG_LOAD_TIMEOUT) {
              console.log('Fragment loading error, attempting to recover...');
              setLoadingMessage("Network issue, recovering...");
              
              if (!data.fatal) {
                // Non-fatal error - HLS.js will try to recover automatically
                setIsBuffering(true);
                
                // Force level switch to a lower quality on repeated buffering
                if (hls && lastBufferingStateRef.current && hls.currentLevel > 0) {
                  console.log('Repeated buffering detected, switching to lower quality');
                  try {
                    hls.nextLevel = Math.max(0, hls.currentLevel - 1);
                  } catch (err) {
                    console.error("Error switching to lower quality:", err);
                  }
                }
                
                // Store current buffering state for next error
                lastBufferingStateRef.current = true;
                return;
              }
            }
          }
          
          if (data.fatal && hls) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log('Fatal network error, attempting to recover');
                setLoadingMessage(`Network error, recovery attempt ${recoveryAttempts + 1}/${maxRecoveryAttempts}...`);
                
                if (recoveryAttempts < maxRecoveryAttempts) {
                  recoveryAttempts++;
                  console.log(`Recovery attempt ${recoveryAttempts}/${maxRecoveryAttempts}`);
                  
                  // More aggressive recovery for network errors
                  setTimeout(() => {
                    if (hls && isMounted) {
                      try {
                        hls.stopLoad();
                        // Try to recover with a lower quality level
                        if (hls.levels && hls.levels.length > 1 && hls.currentLevel !== 0) {
                          hls.nextLevel = 0; // Force lowest quality
                        }
                        hls.startLoad();
                        setIsBuffering(true);
                      } catch (err) {
                        console.error("Error during network error recovery:", err);
                        
                        // Fall back to native implementation if available
                        if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                          try {
                            hls.destroy();
                            hlsRef.current = null;
                            initializeNativeHLS();
                          } catch (fallbackErr) {
                            console.error("Error falling back to native HLS:", fallbackErr);
                            setError('Network error: Cannot load video. Please check your connection and try again.');
                            setIsLoading(false);
                          }
                        } else {
                          setError('Network error: Cannot load video. Please check your connection and try again.');
                          setIsLoading(false);
                        }
                      }
                    }
                  }, 1000);
                } else {
                  // Last attempt, try native fallback if available
                  if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                    try {
                      hls.destroy();
                      hlsRef.current = null;
                      initializeNativeHLS();
                    } catch (fallbackErr) {
                      console.error("Error falling back to native HLS:", fallbackErr);
                      setError('Network error: Cannot load video after multiple attempts. Please check your connection and try again.');
                      setIsLoading(false);
                      setIsBuffering(false);
                    }
                  } else {
                    setError('Network error: Cannot load video after multiple attempts. Please check your connection and try again.');
                    setIsLoading(false);
                    setIsBuffering(false);
                  }
                }
                break;
                 
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('Media error encountered, attempting to recover');
                setLoadingMessage(`Media error, recovery attempt ${recoveryAttempts + 1}/${maxRecoveryAttempts}...`);
                
                if (recoveryAttempts < maxRecoveryAttempts) {
                  recoveryAttempts++;
                  console.log(`Recovery attempt ${recoveryAttempts}/${maxRecoveryAttempts}`);
                  
                  // Try more aggressive media error recovery
                  try {
                    hls.recoverMediaError();
                    
                    // If still having issues, try switching to lowest quality
                    if (recoveryAttempts > 2 && hls.levels && hls.levels.length > 0) {
                      hls.currentLevel = 0; // Force lowest quality
                    }
                    
                    setIsBuffering(true);
                  } catch (err) {
                    console.error("Error during media error recovery:", err);
                    
                    // Try to recreate the HLS instance
                    if (recoveryAttempts >= 3) {
                      try {
                        hls.destroy();
                        hlsRef.current = null;
                        
                        // Create a new instance
                        const newHls = new Hls({
                          maxBufferLength: 30,
                          startLevel: 0, // Start with lowest quality
                          enableWorker: true
                        });
                        
                        newHls.loadSource(url);
                        newHls.attachMedia(videoElement);
                        hlsRef.current = newHls;
                        
                        // Reattach event handlers
                        newHls.on(Hls.Events.MANIFEST_PARSED, () => {
                          videoElement.play().catch(() => {
                            console.error("Failed to play after recreation");
                          });
                        });
                        
                        newHls.on(Hls.Events.ERROR, (event, data) => {
                          if (data.fatal) {
                            // If the new instance also fails, try native HLS
                            if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                              newHls.destroy();
                              hlsRef.current = null;
                              initializeNativeHLS();
                            } else {
                              setError('Media error: Cannot play video. Please try refreshing the page.');
                              setIsLoading(false);
                            }
                          }
                        });
                      } catch (recreateErr) {
                        console.error("Failed to recreate HLS instance:", recreateErr);
                        
                        // Try native HLS as last resort
                        if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                          try {
                            initializeNativeHLS();
                          } catch (nativeErr) {
                            setError('Media error: Cannot play video. Please try another browser.');
                            setIsLoading(false);
                          }
                        } else {
                          setError('Media error: Cannot play video. Please try another browser.');
                          setIsLoading(false);
                        }
                      }
                    }
                  }
                } else {
                  // Try native fallback if available
                  if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                    try {
                      hls.destroy();
                      hlsRef.current = null;
                      initializeNativeHLS();
                    } catch (fallbackErr) {
                      console.error("Error falling back to native HLS:", fallbackErr);
                      setError('Media error: Cannot play video after multiple attempts. Please try another source.');
                      setIsLoading(false);
                      setIsBuffering(false);
                    }
                  } else {
                    setError('Media error: Cannot play video after multiple attempts. Please try another source.');
                    setIsLoading(false);
                    setIsBuffering(false);
                  }
                }
                break;
                 
              default:
                // Cannot recover
                console.log('Fatal error cannot recover');
                try {
                  hls.destroy();
                  hlsRef.current = null;
                  
                  // Try native fallback if available
                  if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                    initializeNativeHLS();
                  } else {
                    setError('Failed to load video. Please try another source or browser.');
                    setIsLoading(false);
                    setIsBuffering(false);
                  }
                } catch (err) {
                  console.error("Error destroying HLS instance after fatal error:", err);
                  setError('Failed to load video. Please try refreshing the page.');
                  setIsLoading(false);
                  setIsBuffering(false);
                }
                break;
            }
          } else if (!data.fatal) {
            // Non-fatal errors - just log them
            console.log('Non-fatal HLS error:', data.details);
            
            // For specific non-fatal errors that might need user feedback
            if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
              setIsBuffering(true);
              setLoadingMessage("Buffering video...");
              
              // Try to switch to lower quality if we keep getting stalled
              if (hls && lastBufferingStateRef.current && hls.currentLevel > 0 && hls.autoLevelEnabled) {
                console.log('Buffer stall detected, hinting to lower quality');
                try {
                  hls.nextLevel = Math.max(0, hls.currentLevel - 1);
                  lastBufferingStateRef.current = true;
                } catch (err) {
                  console.error("Error switching to lower quality after stall:", err);
                }
              }
            }
          }
        });
        
        // Add recovery event handlers
        hls.on(Hls.Events.FRAG_LOADING, () => {
          if (!isMounted) return;
          // Fragment loading started
          if (!videoReady) {
            setLoadingMessage("Loading video segment...");
          }
        });
        
        hls.on(Hls.Events.FRAG_LOADED, () => {
          if (!isMounted) return;
          // Fragment successfully loaded, clear buffering state
          setIsBuffering(false);
          lastBufferingStateRef.current = false;
        });
        
        return () => {
          isMounted = false;
          clearTimeout(manifestTimeout);
          if (hls) {
            console.log("Cleaning up HLS instance on unmount");
            try {
              hls.stopLoad();
              hls.detachMedia();
              hls.destroy();
            } catch (err) {
              console.error("Error cleaning up HLS instance:", err);
            }
            hlsRef.current = null;
          }
        };
      } catch (err) {
        console.error("Error initializing HLS:", err);
        
        // Try native HLS if available
        if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
          console.log("Falling back to native HLS playback");
          initializeNativeHLS();
        } else {
          setError('Failed to initialize video player. Please try a different browser.');
          setIsLoading(false);
        }
        
        return () => {
          isMounted = false;
        };
      }
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari, iOS, etc.)
      initializeNativeHLS();
      
      return () => {
        isMounted = false;
        videoElement.removeEventListener('loadedmetadata', () => {});
        videoElement.removeEventListener('error', () => {});
      };
    } else {
      // No HLS support at all
      setError('Your browser does not support HLS video playback. Please try Firefox, Safari, or Chrome.');
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }
  }, [token, autoPlay]);
  
  // Preload all icons when component mounts
  useEffect(() => {
    // Function to preload an image with timeout
    const preloadImage = (src, timeout = 5000) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        // Set a timeout to avoid hanging if image load takes too long
        const timeoutId = setTimeout(() => {
          reject(new Error(`Timeout loading image: ${src}`));
        }, timeout);
        
        img.onload = () => {
          clearTimeout(timeoutId);
          preloadedIcons.current.add(src);
          resolve(src);
        };
        
        img.onerror = (err) => {
          clearTimeout(timeoutId);
          reject(err);
        };
        
        // Set src after adding event handlers
        img.src = src;
      });
    };
    
    // Preload all icons in parallel
    const preloadAllIcons = async () => {
      try {
        const iconUrls = Object.values(iconPaths);
        await Promise.all(iconUrls.map(url => preloadImage(url)));
        setIconsPreloaded(true);
        console.log('All player icons preloaded successfully');
      } catch (error) {
        console.warn('Failed to preload some player icons:', error);
        // Continue anyway - the browser will load them when needed
        setIconsPreloaded(true);
      }
    };
    
    // Start preloading
    preloadAllIcons();
    
    // Also preload the loading/buffering gifs
    preloadImage('/running.gif').catch(err => 
      console.warn('Failed to preload running.gif:', err)
    );
  }, []);
  
  // event listeners
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    
    // initial volume and muted state
    videoElement.volume = volume;
    videoElement.muted = isMuted;
    videoElement.loop = isLoopEnabled;
    videoElement.playbackRate = playbackSpeed;
    
    // For browsers that support it, enable faster seeking
    if ('fastSeek' in videoElement) {
      videoElement.preload = 'auto';
    }
    
    const handlePlay = () => {
      console.log("Video play event triggered");
      setIsPlaying(true);
      setHasEnded(false);
      if (onPlayPause) onPlayPause(true);
    };
    
    const handlePause = () => {
      console.log("Video pause event triggered");
      setIsPlaying(false);
      if (onPlayPause) onPlayPause(false);
    };
    
    const handleTimeUpdate = () => {
      if (videoElement.currentTime > 0 && videoElement.duration > 0) {
        setCurrentTime(videoElement.currentTime);
        
        // Save progress locally for persistence
        if (token) {
          try {
            // Only save if meaningful progress (> 1 second)
            if (videoElement.currentTime > 1) {
              const progress = {
                time: videoElement.currentTime,
                duration: videoElement.duration
              };
              localStorage.setItem(`video_progress_${token}`, JSON.stringify(progress));
            }
          } catch (err) {
            // Silent fail for localStorage errors
          }
        }
        
        if (onTimeUpdate) {
          onTimeUpdate(videoElement.currentTime, videoElement.duration);
        }
      }
      
      // Clear buffering if we're playing and time is advancing
      if (isBuffering && videoElement.readyState >= 3) {
        setIsBuffering(false);
        lastBufferingStateRef.current = false;
      }
    };
    
    const handleDurationChange = () => {
      if (videoElement.duration && !isNaN(videoElement.duration)) {
        setDuration(videoElement.duration);
      }
    };
    
    const handleProgress = () => {
      if (videoElement.buffered.length > 0) {
        const lastBufferedEnd = videoElement.buffered.end(videoElement.buffered.length - 1);
        setBufferedTime(lastBufferedEnd);
        
        // If we have a good buffer ahead, clear buffering state
        if (isBuffering && lastBufferedEnd > videoElement.currentTime + 3) {
          setIsBuffering(false);
          lastBufferingStateRef.current = false;
        }
      }
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setHasEnded(true);
      
      if (onEnded) {
        onEnded();
      }
    };
    
    const handleLoadStart = () => {
      setIsLoading(true);
      setVideoReady(false);
      setLoadingMessage("Loading video stream...");
    };
    
    const handleCanPlay = () => {
      setLoadingMessage("Ready to play");
      // Don't hide loading immediately, give it a slight delay
      setTimeout(() => {
        setIsLoading(false);
        setVideoReady(true);
        if (onReady) onReady();
      }, 500);
    };
    
    const handleWaiting = () => {
      // to avoid flickering for very short buffering events
      if (bufferingTimerRef.current) {
        clearTimeout(bufferingTimerRef.current);
      }
      
      // Set buffering state after a short delay to avoid flickering
      // but make it shorter for better responsiveness
      bufferingTimerRef.current = setTimeout(() => {
        // Only show buffering if video is not at the end
        if (videoRef.current && videoRef.current.currentTime < videoRef.current.duration - 0.5) {
          console.log("Buffering started");
          setIsBuffering(true);
          lastBufferingStateRef.current = true;
        }
      }, 300); // Reduced from 500ms to 300ms for better responsiveness
    };
    
    const handlePlaying = () => {
      // Clear the buffering timer and hide the indicator
      if (bufferingTimerRef.current) {
        clearTimeout(bufferingTimerRef.current);
        bufferingTimerRef.current = null;
      }
      
      // If buffering was active, log that it ended
      if (isBuffering) {
        console.log("Buffering ended");
      }
      
      setIsBuffering(false);
      lastBufferingStateRef.current = false;
      
      // Ensure loading is hidden when playing starts
      if (isLoading && videoReady) {
        setIsLoading(false);
      }
    };
    
    const handleError = (e) => {
      console.error("Video error:", e);
      setIsLoading(false);
      setError('An error occurred while playing the video.');
    };
    
    const handleFullscreenChange = () => {
      const isFullscreenActive = !!document.fullscreenElement ||
                                !!document.webkitFullscreenElement ||
                                !!document.mozFullScreenElement ||
                                !!document.msFullscreenElement;
      
      setIsFullscreen(isFullscreenActive);
      if (onFullscreenToggle) {
        onFullscreenToggle(isFullscreenActive);
      }
    };
    
    //event listeners
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('durationchange', handleDurationChange);
    videoElement.addEventListener('progress', handleProgress);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('loadstart', handleLoadStart);
    videoElement.addEventListener('canplay', handleCanPlay);
    videoElement.addEventListener('waiting', handleWaiting);
    videoElement.addEventListener('playing', handlePlaying);
    videoElement.addEventListener('error', handleError);
    
    // Add all browser-specific fullscreen change events
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    // Clean up
    return () => {
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('durationchange', handleDurationChange);
      videoElement.removeEventListener('progress', handleProgress);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('loadstart', handleLoadStart);
      videoElement.removeEventListener('canplay', handleCanPlay);
      videoElement.removeEventListener('waiting', handleWaiting);
      videoElement.removeEventListener('playing', handlePlaying);
      videoElement.removeEventListener('error', handleError);
      
      // Remove all browser-specific fullscreen change events
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      
      if (bufferingTimerRef.current) {
        clearTimeout(bufferingTimerRef.current);
      }
    };
  }, [volume, isMuted, isLoopEnabled, playbackSpeed, onPlayPause, onTimeUpdate, onEnded, onReady, onFullscreenToggle, isLoading, videoReady]);
  
  // Update loop and autoNext when props change
  useEffect(() => {
    setIsLoopEnabled(loop);
    if (videoRef.current) {
      videoRef.current.loop = loop;
    }
  }, [loop]);
  
  useEffect(() => {
    setIsAutoNextEnabled(autoNext);
  }, [autoNext]);
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!videoRef.current) return;
      
      // Don't process shortcuts if any of these conditions are met:
      // 1. Input or textarea is focused
      // 2. Any element within a modal is focused or active (modals typically have high z-index)
      // 3. Search input is focused
      
      const activeElement = document.activeElement;
      const isInputFocused = activeElement.tagName === 'INPUT' || 
                             activeElement.tagName === 'TEXTAREA' || 
                             activeElement.isContentEditable;
      
      // Check if search input is focused
      const isSearchFocused = activeElement.tagName === 'INPUT' && 
                                 (activeElement.placeholder === 'Search Everything' ||
                                  activeElement.classList.contains('search-input'));
      
      // Check if any modal is open
      const isModalOpen = document.querySelector('.modal-overlay, [role="dialog"], .ModalOverlay');
      
      // Check if event originated from a modal (report or suggestion)
      const isModalEvent = e.target.closest('.ModalOverlay, .ModalContainer, [role="dialog"]');
      
      // Check if event came from a modal input
      const isModalInput = isInputFocused && (
        e.target.closest('.ModalOverlay') || 
        e.target.closest('.ModalContainer') || 
        e.target.closest('[role="dialog"]')
      );
      
      // Don't handle keyboard events if any of these conditions are true
      if (isInputFocused || isSearchFocused || isModalOpen || isModalEvent || isModalInput) {
        return;
      }
      
      // Close modals on Escape key
      if (e.key === 'Escape' && (showSettings || showQualityMenu || showSpeedMenu)) {
        closeAllModals();
        e.preventDefault();
        return;
      }
      
      switch (e.key) {
        case ' ':
        case 'k':
          togglePlayPause(e);
          e.preventDefault();
          break;
        case 'f':
          toggleFullscreen();
          e.preventDefault();
          break;
        case 'm':
          toggleMute();
          e.preventDefault();
          break;
        case 'ArrowRight':
          closeAllModals();
          videoRef.current.currentTime = Math.min(
            videoRef.current.duration,
            videoRef.current.currentTime + 10
          );
          e.preventDefault();
          break;
        case 'ArrowLeft':
          closeAllModals();
          videoRef.current.currentTime = Math.max(
            0,
            videoRef.current.currentTime - 10
          );
          e.preventDefault();
          break;
        case 'ArrowUp':
          closeAllModals();
          setVolume(Math.min(1, volume + 0.1));
          videoRef.current.volume = Math.min(1, volume + 0.1);
          if (isMuted) {
            setIsMuted(false);
            videoRef.current.muted = false;
          }
          e.preventDefault();
          break;
        case 'ArrowDown':
          closeAllModals();
          setVolume(Math.max(0, volume - 0.1));
          videoRef.current.volume = Math.max(0, volume - 0.1);
          e.preventDefault();
          break;
        default:
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [volume, isMuted, showSettings, showQualityMenu, showSpeedMenu, togglePlayPause, toggleFullscreen, toggleMute]);
  
  // when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if ((showSettings && 
          settingsBtnRef.current && 
          !settingsBtnRef.current.contains(e.target) &&
          e.target.closest('.settings-menu') === null) ||
          (showQualityMenu &&
          qualityBtnRef.current &&
          !qualityBtnRef.current.contains(e.target) &&
          e.target.closest('.quality-menu') === null) ||
          (showSpeedMenu &&
          speedBtnRef.current &&
          !speedBtnRef.current.contains(e.target) &&
          e.target.closest('.speed-menu') === null)) {
        closeAllModals();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings, showQualityMenu, showSpeedMenu]);
  
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        const screenHeight = window.innerHeight;
        // For shorter screens, always place menus at top
        if (screenHeight < 600) {
          setIsMobile(false); // Force top positioning on short screens
        } else {
          setIsMobile(window.innerWidth <= 768);
        }
      }
    };
    
    // Initial check
    checkScreenSize();
    
    // Add event listener
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Create an explicit check for whether to render menus at the top regardless of device
  // This is useful in landscape mode or short screens
  const renderAtTop = !isMobile || (containerRef.current && containerRef.current.offsetHeight < 500);
  
  const renderSettingsMenu = () => {
    if (!showSettings) return null;
    
    return (
      <>
        <SettingsOverlay onClick={() => setShowSettings(false)} />
        <SettingsMenu className="settings-menu" $fromBottom={isMobile && !renderAtTop}>
          <SettingsItem onClick={(e) => {
            e.stopPropagation();
            toggleSpeedMenu(e);
            setShowSettings(false);
          }}>
            <SettingsLabel>
              <Icon icon="speedometer" size="28px" color="white" /> Speed
            </SettingsLabel>
            <SettingsValue $active={playbackSpeed !== 1.0}>{playbackSpeed}x</SettingsValue>
          </SettingsItem>
          
          <SettingsItem onClick={(e) => {
            e.stopPropagation();
            toggleLoop(e);
          }}>
            <SettingsLabel>
              <Icon icon="loop" size="28px" color="white" /> Loop
            </SettingsLabel>
            <SettingsValue $active={isLoopEnabled}>{isLoopEnabled ? 'On' : 'Off'}</SettingsValue>
          </SettingsItem>
          
          <SettingsItem onClick={(e) => {
            e.stopPropagation();
            toggleAutoNext(e);
          }}>
            <SettingsLabel>
              <Icon icon="stepForward" size="28px" color="white" /> Auto Next
            </SettingsLabel>
            <SettingsValue $active={isAutoNextEnabled}>{isAutoNextEnabled ? 'On' : 'Off'}</SettingsValue>
          </SettingsItem>
        </SettingsMenu>
      </>
    );
  };

  // quality selector menu
  const renderQualityMenu = () => {
    if (!showQualityMenu) return null;
    
    // only show auto if no other qualities available
    const displayQualities = availableQualities.length > 1 ? availableQualities : ['auto'];
    
    // On mobile, limit the displayed text length
    const formatQualityLabel = (quality) => {
      if (quality === 'auto') {
        if (!isMobile) {
          return autoSelectedQuality ? `Auto (${autoSelectedQuality})` : 'Auto';
        }
        return autoSelectedQuality ? `Auto (${autoSelectedQuality})` : 'Auto';
      }
      
      return quality; // e.g. '720p'
    };
    
    return (
      <>
        <SettingsOverlay onClick={() => setShowQualityMenu(false)} />
        <QualityMenu className="quality-menu" $fromBottom={isMobile && !renderAtTop}>
          {displayQualities.map(quality => (
            <QualityItem key={quality} onClick={(e) => {
              e.stopPropagation();
              changeQuality(quality);
            }}>
              <QualityLabel $active={currentQuality === quality}>
                {formatQualityLabel(quality)}
              </QualityLabel>
              <CheckIcon $visible={currentQuality === quality}>
                <Icon icon="check" size="16px" color="white" />
              </CheckIcon>
            </QualityItem>
          ))}
        </QualityMenu>
      </>
    );
  };
  
  // render playback speed menu with even fewer options on mobile
  const renderSpeedMenu = () => {
    if (!showSpeedMenu) return null;
    
    // More compact list of speeds for different screen sizes
    const compactSpeeds = isMobile ? [0.5, 1.0, 1.5, 2.0] : [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
    
    return (
      <>
        <SettingsOverlay onClick={() => setShowSpeedMenu(false)} />
        <SpeedMenu className="speed-menu" $fromBottom={isMobile && !renderAtTop}>
          {compactSpeeds.map(speed => (
            <SpeedItem key={speed} onClick={(e) => {
              e.stopPropagation();
              changePlaybackSpeed(speed);
            }}>
              <SpeedLabel $active={playbackSpeed === speed}>
                {speed}x
              </SpeedLabel>
              <CheckIcon $visible={playbackSpeed === speed}>
                <Icon icon="check" size="16px" color="white" />
              </CheckIcon>
            </SpeedItem>
          ))}
        </SpeedMenu>
      </>
    );
  };
  
  const [lastTap, setLastTap] = useState({ time: 0, x: 0, y: 0 });
  const [doubleTapSide, setDoubleTapSide] = useState(null);
  
  const handleVideoClick = (e) => {
    e.stopPropagation();
    
    // Don't respond to clicks when loading or not ready
    if (!videoReady || isLoading) return;
    
    // If settings or quality menu is open close them and do nothing else
    if (showSettings || showQualityMenu || showSpeedMenu) {
      closeAllModals();
      return;
    }
    
    const now = Date.now();
    const x = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const screenWidth = window.innerWidth;
    const screenSide = x < screenWidth / 3 ? 'left' : x > (screenWidth * 2) / 3 ? 'right' : 'center';
    
    // Check if controls were already visible before this tap
    const controlsWereVisible = showControls;
    
    // Always show controls on tap
    resetControlsTimer();
    
    // Check if this is a double tap (within 300ms)
    if (now - lastTap.time < 300) {
      // This is a double-tap
      if (screenSide === 'left') {
        handleDoubleTap('backward');
        setDoubleTapSide('left');
      } else if (screenSide === 'right') {
        handleDoubleTap('forward');
        setDoubleTapSide('right');
      } else {
        // Doubletap in center
        togglePlayPause(e);
      }
      
      // Reset tap state after handling doubletap
      setLastTap({ time: 0, x: 0, y: 0 });
      
      // Clear pending singletap timeout
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
        tapTimeoutRef.current = null;
      }
    } else {
      // This is a single tap store it and set a timeout
      setLastTap({ time: now, x, y: e.clientY });
      
      // Clear any existing timeout
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
      
      // Set a timeout to handle single tap if no second tap comes
      tapTimeoutRef.current = setTimeout(() => {
        // First tap or tap when controls are hidden should only show controls
        // Only toggle play/pause if controls were already visible before the tap
        if (screenSide === 'center' && controlsWereVisible) {
          togglePlayPause(e);
        }
        tapTimeoutRef.current = null;
      }, 300);
    }
  };
  
  // Clean up
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, []);
  
  const handleProgressBarHover = (e) => {
    if (!progressBarRef.current || !videoRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const position = e.clientX - rect.left;
    const percentage = position / rect.width;
    const time = percentage * duration;
    
    setTooltipPosition(position);
    setTooltipTime(time);
    setIsHoveringProgress(true);
  };

  const handleProgressBarLeave = () => {
    setIsHoveringProgress(false);
  };
  
  // Handle image loading errors
  const handleLoadingImageError = () => {
    setLoadingImageError(true);
  };
  
  const handleBufferingImageError = () => {
    setBufferingImageError(true);
  };

  // Reset controls timer on inactivity
  useEffect(() => {
    resetControlsTimer();
    
    // checking inactivity every second with improved performance
    const inactivityCheckInterval = setInterval(() => {
      const now = Date.now();
      const inactiveTime = now - lastActivityRef.current;
      
      // more than 3 seconds and playing, hide controls
      if (inactiveTime > 3000 && isPlaying && !showSettings && !showQualityMenu && !showSpeedMenu && !seekingRef.current) {
        setShowControls(false);
      }
    }, 1000);
    
    return () => {
      if (controlsTimerRef.current) {
        clearTimeout(controlsTimerRef.current);
      }
      clearInterval(inactivityCheckInterval);
    };
  }, [isPlaying, showSettings, showQualityMenu, showSpeedMenu]);
  
  // Clean up when component unmounts or token changes
  useEffect(() => {
    return () => {
      // Safety clean up - more defensive programming
      try {
        // Clean up fullscreen if component unmounts while in fullscreen
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(err => {
            console.warn('Error exiting fullscreen on unmount:', err);
          });
        }
        
        // Unlock screen orientation if locked
        if (isRotationSupported && window.screen && window.screen.orientation) {
          try {
            const orientation = window.screen.orientation || 
                              window.screen.mozOrientation || 
                              window.screen.msOrientation;
            if (orientation && orientation.unlock) {
              orientation.unlock();
            }
          } catch (err) {
            console.warn('Error unlocking screen orientation on unmount:', err);
          }
        }
      } catch (err) {
        console.warn('Error during player cleanup:', err);
      }
    };
  }, [isRotationSupported]);
  
  return (
    <PlayerContainer 
      ref={containerRef} 
      onMouseMove={handleMouseMove}
      onClick={handleVideoClick}
    >
      {/* Icon Preloader - hidden component that forces browser to cache all icons */}
      <IconPreloader aria-hidden="true">
        {Object.values(iconPaths).map((path, index) => (
          <img key={index} src={path} alt="" />
        ))}
        <img src="/running.gif" alt="" />
      </IconPreloader>
      
      <VideoElement 
        ref={videoRef}
        playsInline
        onDoubleClick={toggleFullscreen}
      />
      
      <ControlsOverlay $visible={(showControls || !isPlaying || showSettings || showQualityMenu || showSpeedMenu || hasEnded) && !isLoading}>
        <TopControls>
          <Title>
            {isPlaying ? `Now Playing | ${formatTitleDisplay()}` : `Paused | ${formatTitleDisplay()}`}
          </Title>
          <TopRightControls>
            {availableQualities.length > 1 && (
              <QualityButton 
                ref={qualityBtnRef}
                onClick={toggleQualityMenu}
                data-tooltip="Quality"
              >
                <Icon icon="list" size="26px" color="white" />
              </QualityButton>
            )}
            <SettingsButton 
              ref={settingsBtnRef}
              onClick={toggleSettings}
              data-tooltip="Settings"
            >
              <Icon icon="settings" size="28px" color="white" />
            </SettingsButton>
          </TopRightControls>
        </TopControls>
        
        <BottomControls>
          <ProgressBarContainer
            ref={progressBarRef}
            onClick={handleProgressBarClick}
            onMouseMove={handleProgressBarHover}
            onMouseLeave={handleProgressBarLeave}
          >
            <TimeTooltip 
              $position={tooltipPosition}
              $visible={isHoveringProgress}
            >
              {formatTime(tooltipTime)}
            </TimeTooltip>
            <ProgressBar>
              <BufferedProgress $width={`${getBufferedPercentage()}%`} />
              <CurrentProgress $width={`${getProgressPercentage()}%`} />
              <ProgressHandle 
                $left={`${getProgressPercentage()}%`} 
                $visible={showControls}
              />
            </ProgressBar>
          </ProgressBarContainer>
          
          <ControlButtons>
            <LeftControls>
              <PlayButton 
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlayPause(e);
                }}
                data-tooltip={isPlaying ? "Pause" : "Play"}
              >
                <Icon icon={isPlaying ? "pause" : "play"} size="28px" color="white" />
              </PlayButton>
              
              {/* Add Previous Button */}
              {onPrevious && (
                <NavigationButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onPrevious();
                  }}
                  data-tooltip="Previous"
                >
                  <Icon icon="backward" size="24px" color="white" />
                </NavigationButton>
              )}
              
              {/* Add Next Button */}
              {onNext && (
                <NavigationButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                  }}
                  data-tooltip="Next"
                >
                  <Icon icon="forward" size="24px" color="white" />
                </NavigationButton>
              )}
              
              <TimeDisplay>
                {formatTime(currentTime)} / {formatTime(duration)}
              </TimeDisplay>
              
              <VolumeControl>
                <ControlButton 
                  onClick={toggleMute}
                  data-tooltip={isMuted ? "Unmute" : "Mute"}
                >
                  <Icon icon={
                    isMuted || volume === 0 
                      ? "volumeMute" 
                      : volume < 0.5 
                        ? "volumeDown" 
                        : "volumeUp"
                  } size="26px" color="white" />
                </ControlButton>
                <VolumeSliderContainer>
                  <VolumeSlider 
                    type="range" 
                    min="0" 
                    max="1" 
                    step="0.1" 
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                </VolumeSliderContainer>
              </VolumeControl>
            </LeftControls>
            
            <RightControls>
              <ControlButton 
                onClick={toggleFullscreen}
                data-tooltip={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
              >
                <Icon icon={isFullscreen ? "compress" : "expand"} size="26px" color="white" />
              </ControlButton>
            </RightControls>
          </ControlButtons>
        </BottomControls>
      </ControlsOverlay>
      
      {/* Centered Play/Pause Button */}
      <CenteredPlayButton 
        $visible={showControls && !showSettings && !showQualityMenu && !showSpeedMenu && !isLoading && !isBuffering && videoReady}
        onClick={(e) => {
          e.stopPropagation();
          togglePlayPause(e);
        }}
      >
        <Icon icon={isPlaying ? "pause" : "play"} size="56px" color="white" />
      </CenteredPlayButton>
      
      {/* Settings Menu */}
      {renderSettingsMenu()}
      
      {/* Quality Menu */}
      {renderQualityMenu()}
      
      {/* Speed Menu */}
      {renderSpeedMenu()}
      
      {/* Buffering Indicator */}
      <BufferingIndicator $visible={isBuffering && !isLoading}>
        {bufferingImageError ? (
          <Icon icon="settings" size="48px" color="white" spin />
        ) : (
          <BufferingGif 
            src="/running.gif" 
            alt="Buffering..." 
            onError={handleBufferingImageError}
            className={bufferingImageError ? 'fallback' : ''}
          />
        )}
        <BufferingText>Buffering...</BufferingText>
      </BufferingIndicator>
      
      {/* Loading Overlay */}
      {isLoading && (
        <LoadingOverlay>
          {loadingImageError ? (
            <Icon icon="settings" size="70px" color="white" spin />
          ) : (
            <RunningGif 
              src="/running.gif" 
              alt="Loading..." 
              onError={handleLoadingImageError}
              className={loadingImageError ? 'fallback' : ''}
            />
          )}
          <LoadingText>{loadingMessage}</LoadingText>
        </LoadingOverlay>
      )}
      
      {/* Error Overlay */}
      {error && (
        <ErrorOverlay>
          <ErrorIcon>
            <Icon icon="warning" size="48px" color="white" />
          </ErrorIcon>
          <ErrorMessage>{error}</ErrorMessage>
          <ErrorButton onClick={handleRetry}>Try Again</ErrorButton>
        </ErrorOverlay>
      )}
      
      {/* Ended Overlay */}
      {hasEnded && !isLoopEnabled && (
        <EndedOverlay>
          <ReplayButton onClick={handleReplay}>
            <Icon icon="play" size="42px" color="white" />
          </ReplayButton>
          
          {isAutoNextEnabled && (
            <NextButton onClick={() => onEnded && onEnded()}>
              <Icon icon="stepForward" size="28px" color="white" /> Next Episode
            </NextButton>
          )}
          
          {/* Always show Next button regardless of autoNextEnabled setting */}
          {!isAutoNextEnabled && (
            <NextButton onClick={() => onEnded && onEnded()}>
              <Icon icon="stepForward" size="28px" color="white" /> Next Episode
            </NextButton>
          )}
        </EndedOverlay>
      )}
      
      {/* Add Continue Button */}
      {showContinueButton && savedProgress && (
        <ContinueButton 
          ref={continueButtonRef}
          onClick={handleContinue}
        >
          <Icon icon="play" size="24px" color="white" />
          Continue from {formatTime(savedProgress.time)}
        </ContinueButton>
      )}
    </PlayerContainer>
  );
});

export default Player;
